export interface LatestJobTimers {
  setTimeout(handler: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface LatestJobContext {
  jobId: number;
  signal: AbortSignal;
  invalidationReason: string;
}

export interface LatestJobSchedulerOptions<T> {
  debounceMs: number;
  timers?: LatestJobTimers;
  run(context: LatestJobContext): T | Promise<T>;
  onResult(result: T, context: LatestJobContext): void;
  onError?(error: unknown, context: LatestJobContext): void;
}

const defaultTimers: LatestJobTimers = {
  setTimeout: (handler, ms) => setTimeout(handler, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export class LatestJobScheduler<T> {
  private debounceMs: number;
  private readonly timers: LatestJobTimers;
  private readonly run: LatestJobSchedulerOptions<T>["run"];
  private readonly onResult: LatestJobSchedulerOptions<T>["onResult"];
  private readonly onError: NonNullable<LatestJobSchedulerOptions<T>["onError"]>;
  private timer: unknown;
  private controller: AbortController | null = null;
  private latestJobId = 0;

  constructor(options: LatestJobSchedulerOptions<T>) {
    this.debounceMs = Math.max(0, options.debounceMs);
    this.timers = options.timers ?? defaultTimers;
    this.run = options.run;
    this.onResult = options.onResult;
    this.onError = options.onError ?? (() => {});
  }

  schedule(invalidationReason = "input_changed"): number {
    const jobId = ++this.latestJobId;
    this.cancelPendingTimer();
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;

    this.timer = this.timers.setTimeout(() => {
      this.timer = undefined;
      void this.execute(jobId, controller, invalidationReason);
    }, this.debounceMs);

    return jobId;
  }

  setDebounceMs(ms: number): void {
    this.debounceMs = Math.max(0, ms);
  }

  cancel(): void {
    this.cancelPendingTimer();
    this.controller?.abort();
    this.controller = null;
    this.latestJobId++;
  }

  dispose(): void {
    this.cancel();
  }

  private async execute(
    jobId: number,
    controller: AbortController,
    invalidationReason: string,
  ): Promise<void> {
    const context: LatestJobContext = {
      jobId,
      signal: controller.signal,
      invalidationReason,
    };
    try {
      const result = await this.run(context);
      if (this.isStale(jobId, controller)) return;
      this.onResult(result, context);
    } catch (error) {
      if (this.isStale(jobId, controller)) return;
      this.onError(error, context);
    }
  }

  private isStale(jobId: number, controller: AbortController): boolean {
    return controller.signal.aborted || jobId !== this.latestJobId;
  }

  private cancelPendingTimer(): void {
    if (this.timer !== undefined) {
      this.timers.clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}
